import { nanoid } from 'nanoid';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import SearchSVG from '@/assets/icons/search.svg';
import { useModal } from '@/components/common/modal';

import { IBatch } from '../../types';
import { TaskEditModal } from '../task-edit-modal';
import { IImageTask, TaskImageModal } from '../task-image-modal';

export const PictureCardContainer: React.FC<{ batch: IBatch }> = ({ batch }) => {
  const { isOpen, toggle } = useModal();
  const { isOpen: isTaskEditOpen, toggle: toggleTaskEdit } = useModal();
  const { t } = useTranslation();

  // we use below unique string to re-render task edit image modal
  // so that the modal will always have latest state of the "task" data
  const [modalKey, setModalKey] = useState('');
  const [modalKeyTaskModal, setModalKeyTaskModal] = useState('__');

  // once user clicks on a task image, we set relevant task as the selected/current task
  const [selectedTask, setSelectedTask] = useState<any>(null);

  // index of selected task in the tasks array.
  // we need this to manage 'next' and 'previous' functions
  const selectedTaskIndex = useMemo(
    () =>
      selectedTask && batch.tasks
        ? batch.tasks.findIndex((t) => t?.id === selectedTask.id)
        : 0,
    [batch, selectedTask],
  );

  // once user click on source/result comparison component, we open the modal with bit large comparison
  const handleOnPictureClick = (task: IImageTask) => {
    setSelectedTask(task);
    toggle();
    setModalKeyTaskModal(nanoid());
  };

  const handleOnNext = () => {
    if (!batch.tasks) return;
    const len = batch.tasks?.length ?? 0;
    const next = selectedTaskIndex === len - 1 ? 0 : selectedTaskIndex + 1;
    setSelectedTask(batch.tasks[next]);
  };

  const handleOnPrevious = () => {
    if (!batch.tasks) return;
    const len = batch.tasks?.length ?? 0;
    const prev = selectedTaskIndex === 0 ? len - 1 : selectedTaskIndex - 1;
    setSelectedTask(batch.tasks[prev]);
  };

  const handleOnToggleTaskModal = () => {
    toggle();
    setModalKeyTaskModal(nanoid());
  };

  const handleOnToggleTaskEdit = () => {
    toggleTaskEdit();
    setModalKey(nanoid());
  };

  const renderPictureCards = () => {
    return (
      batch.tasks &&
      batch.tasks.map((task) => {
        return (
          <div
            key={task.id}
            className="flex gap-10 transition duration-300 ease-in-out step-individual-image"
          >
            <div className="flex-1">
              <button onClick={() => handleOnPictureClick(task)} className="relative">
                <img src={`${task.resultImage}?no-cache=${nanoid()}`} alt="result" />
                <div className="absolute bottom-0 right-0 p-2 bg-white opacity-50">
                  <img src={SearchSVG} alt="search" className="w-5 h-5" />
                </div>
              </button>
            </div>
            <div className="flex flex-col flex-1 gap-4">
              {task?.resultStat.countTotal < 10 ? (
                <p className="italic font-semibold text-red-light">
                  {t('warnings.tooFewCells')}
                </p>
              ) : null}

              {task?.resultStat.countTotal >= 10 &&
              task.resultStat.imageQuality &&
              task.resultStat.imageQuality.length > 0
                ? task.resultStat.imageQuality.map((warning) => (
                    <p
                      key={`${task.id}-${warning}`}
                      className="italic font-semibold text-red-light"
                    >
                      {t(`warnings.${warning}`)}
                    </p>
                  ))
                : null}
              <div className="flex flex-col gap-6">
                <DataItem label="Alive" value={task?.resultStat.countAlive} />
                <DataItem label="Dead" value={task?.resultStat.countDead} />
                <DataItem label="Budding" value={task?.resultStat.countBudding} />
                <DataItem label="Total" value={task?.resultStat.countTotal} />
              </div>
            </div>
          </div>
        );
      })
    );
  };

  const DataItem = ({
    label,
    value,
  }: {
    label: 'Alive' | 'Dead' | 'Budding' | 'Total';
    value: string | number | undefined;
  }) => {
    const _colors = {
      Alive: '#9EC597',
      Dead: '#E27ADC',
      Budding: '#E2CE7A',
      Total: '#FFF',
    };
    const colorClass = _colors[label];

    return (
      <div className="flex w-full">
        <div className="flex items-center flex-1 gap-3 font-bold text-black-dark">
          <div
            style={{ backgroundColor: colorClass }}
            className={'w-3 h-3 rounded-full'}
          ></div>
          <span>{label}</span>
        </div>
        <span className="flex-1 text-black-dark">
          {value === undefined ? 'N/A' : `${value} cells`}
        </span>
      </div>
    );
  };

  const onEditCells = () => {
    setModalKey(nanoid());
    toggleTaskEdit();
  };

  return (
    <>
      <TaskImageModal
        batch={batch}
        isOpen={isOpen}
        task={selectedTask}
        onNext={handleOnNext}
        key={modalKeyTaskModal}
        index={selectedTaskIndex}
        onPrevious={handleOnPrevious}
        onEdit={onEditCells}
        toggle={handleOnToggleTaskModal}
      />
      <TaskEditModal
        key={modalKey}
        batch={batch}
        task={selectedTask}
        toggle={handleOnToggleTaskEdit}
        isOpen={isTaskEditOpen}
      />
      <div className="flex flex-col gap-5 mt-10">{renderPictureCards()}</div>
    </>
  );
};
